import { Box, Grid, InputAdornment, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { FC, useCallback, useState } from 'react';
import type { SetShowSnackbar } from '../../types/alerts';
import type { DownloadReport } from '../../types/reports';

import { dayjs, formatDateParkerStatus } from '../../utils';
import { ReportActionButton } from './reportActionButton';

interface TransactionReportProps {
	facilityId: string;
	setShowSnackbar: SetShowSnackbar;
	downloadReport: DownloadReport;
}

export const TransactionReportForm: FC<TransactionReportProps> = ({ facilityId, setShowSnackbar, downloadReport }) => {
	const [invoiceCreditId, setInvoiceCreditId] = useState<string>();
	const [subName, setSubName] = useState<string>();
	const [accountId, setAccountId] = useState<string>();
	const [startDate, setStartDate] = useState<string>();
	const [endDate, setEndDate] = useState<string>();
	const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false);

	const reportType = 'TransactionReport';
	const isTransactionReport = true;
	const setIsDownloadingPdf = null;

	const handleValidation = useCallback(() => {
		if (dayjs(startDate).isAfter(dayjs(endDate))) {
			setShowSnackbar({
				message: 'Start date cannot be after end date.',
				severity: 'error',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [startDate, endDate, setShowSnackbar]);

	const getDownloadParams = useCallback(() => {
		const baseParams = {
			facilityId,
		};

		return {
			...baseParams,
			...(accountId ? { accountId } : {}),
			...(subName ? { subscriptionName: subName } : {}),
			...(invoiceCreditId ? { invoiceCreditId } : {}),
			...(startDate ? { startDate: formatDateParkerStatus(startDate) } : {}),
			...(endDate ? { endDate: formatDateParkerStatus(endDate) } : {}),
		};
	}, [facilityId, accountId, subName, startDate, endDate, invoiceCreditId]);

	const params = getDownloadParams();

	const handleReset = useCallback(() => {
		setInvoiceCreditId('');
		setStartDate(null);
		setEndDate(null);
		setAccountId('');
		setSubName('');
	}, []);

	return (
		<Box sx={{ padding: 3 }}>
			<Grid container spacing={2}>
				<Grid item xs={4}>
					<TextField
						fullWidth
						variant='outlined'
						label='Invoice/Credit ID'
						value={invoiceCreditId || ''}
						onChange={(e) => setInvoiceCreditId(e.target.value)}
						inputProps={{ 'data-cy': 'invoice-credit-id-input' }}
					/>
				</Grid>
				<Grid item xs={4}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Start Date *'
							value={startDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'start-date-picker' },
								},
							}}
							onChange={setStartDate}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={4}>
					<TextField
						type='number'
						fullWidth
						variant='outlined'
						label='Account ID'
						value={accountId || ''}
						onChange={(e) => setAccountId(e.target.value)}
						inputProps={{ 'data-cy': 'account-id-input' }}
						onKeyDown={(event) => {
							if (event?.key === '-' || event?.key === '+' || event?.key === 'e') {
								event.preventDefault();
							}
						}}
						onPaste={(event) => {
							event.preventDefault();

							const paste = event.clipboardData.getData('text');
							const cleanedPaste = /^[AC]*[-]*(\d+)$/.exec(paste);

							if (cleanedPaste) {
								setAccountId(cleanedPaste[1]);
							}
						}}
						InputProps={{
							startAdornment: <InputAdornment position='start'>AC-</InputAdornment>,
						}}
					/>
				</Grid>
				<Grid item xs={4}>
					<TextField
						fullWidth
						variant='outlined'
						label='Sub Name'
						value={subName || ''}
						onChange={(e) => setSubName(e.target.value)}
						inputProps={{ 'data-cy': 'sub-name-input' }}
					/>
				</Grid>
				<Grid item xs={4}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='End Date'
							value={endDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'end-date-picker' },
								},
							}}
							onChange={setEndDate}
						/>
					</LocalizationProvider>
				</Grid>
			</Grid>
			<ReportActionButton
				downloadReport={(format) =>
					downloadReport(format, {
						reportType: reportType as string,
						params,
						handleValidation,
						setIsDownloadingCsv,
						setIsDownloadingPdf,
					})
				}
				isDownloadingCsv={isDownloadingCsv}
				isTransactionReport={isTransactionReport}
				disableDownloadButtons={!facilityId}
				onReset={handleReset}
			/>
		</Box>
	);
};
